<p-dialog
    header="Generate reviews for all games"
    [visible]="visibleSignal()"
    (visibleChange)="onVisibleChange($event)"
    [style]="{ width: '40rem' }"
    [breakpoints]="{ '1024px': '95vw' }"
    appendTo="body"
    [modal]="true"
    [closable]="!running()"
    [draggable]="false"
>
    @if (finished()) {
    <div class="flex flex-col gap-4">
        <p-message
            [severity]="errorCount() > 0 ? 'warn' : 'success'"
            [text]="
                okCount() +
                ' game(s) updated · ' +
                skippedCount() +
                ' skipped · ' +
                errorCount() +
                ' failed'
            "
            styleClass="m-0"
        />

        <p-table
            [value]="results()"
            [rowTrackBy]="trackByIndex"
            [scrollable]="true"
            scrollHeight="24rem"
            [showGridlines]="true"
        >
            <ng-template #header>
                <tr>
                    <th>Game</th>
                    <th style="width: 7rem">Status</th>
                    <th>Detail</th>
                </tr>
            </ng-template>
            <ng-template #body let-row>
                <tr>
                    <td class="font-medium">{{ row.title }}</td>
                    <td>
                        <p-tag
                            [value]="row.status"
                            [severity]="statusSeverity(row.status)"
                        />
                    </td>
                    <td class="text-sm text-muted-color">{{ row.detail }}</td>
                </tr>
            </ng-template>
        </p-table>
    </div>
    } @else {
    <div class="flex flex-col gap-4">
        <p-message
            severity="warn"
            text="This replaces fake reviews on every game in this realm: existing fake reviews are removed first, then new ones are generated. Real reviews are not affected. Each game uses its own tags."
            styleClass="m-0"
        />

        @if (total() === 0) {
        <p-message
            severity="info"
            text="No games configured for this realm."
            styleClass="m-0"
        />
        } @else {
        <div class="text-sm text-muted-color">
            Each of the <strong>{{ total() }}</strong> game(s) gets a random
            average rating and a random number of reviews drawn from the ranges
            below.
        </div>

        <!-- Average rating range -->
        <div class="flex flex-col gap-1">
            <label class="text-sm font-medium">Average rating range</label>
            <div class="flex items-center gap-2">
                <p-inputnumber
                    inputId="bulk-avg-min"
                    [ngModel]="averageMin()"
                    (ngModelChange)="averageMin.set($event ?? ratingMin)"
                    [min]="ratingMin"
                    [max]="ratingMax"
                    [step]="0.1"
                    mode="decimal"
                    [minFractionDigits]="1"
                    [maxFractionDigits]="2"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    styleClass="flex-1"
                />
                <span class="text-muted-color shrink-0">–</span>
                <p-inputnumber
                    inputId="bulk-avg-max"
                    [ngModel]="averageMax()"
                    (ngModelChange)="averageMax.set($event ?? ratingMax)"
                    [min]="ratingMin"
                    [max]="ratingMax"
                    [step]="0.1"
                    mode="decimal"
                    [minFractionDigits]="1"
                    [maxFractionDigits]="2"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    styleClass="flex-1"
                />
            </div>
        </div>

        <!-- Review count range -->
        <div class="flex flex-col gap-1">
            <label class="text-sm font-medium">Reviews per game</label>
            <div class="flex items-center gap-2">
                <p-inputnumber
                    inputId="bulk-count-min"
                    [ngModel]="countMin()"
                    (ngModelChange)="countMin.set($event ?? 1)"
                    [min]="1"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    styleClass="flex-1"
                />
                <span class="text-muted-color shrink-0">–</span>
                <p-inputnumber
                    inputId="bulk-count-max"
                    [ngModel]="countMax()"
                    (ngModelChange)="countMax.set($event ?? 1)"
                    [min]="1"
                    [showButtons]="true"
                    buttonLayout="horizontal"
                    spinnerMode="horizontal"
                    decrementButtonClass="p-button-secondary"
                    incrementButtonClass="p-button-secondary"
                    styleClass="flex-1"
                />
            </div>
        </div>

        <!-- Created-at range -->
        <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1 min-w-0">
                <label class="text-sm font-medium" for="bulk-from">
                    Created from
                </label>
                <p-datepicker
                    inputId="bulk-from"
                    [ngModel]="createdFrom()"
                    (ngModelChange)="createdFrom.set($event ?? null)"
                    dateFormat="yy-mm-dd"
                    [showIcon]="true"
                    [showButtonBar]="true"
                    appendTo="body"
                    styleClass="w-full"
                />
            </div>
            <div class="flex flex-col gap-1 min-w-0">
                <label class="text-sm font-medium" for="bulk-to">
                    Created to
                </label>
                <p-datepicker
                    inputId="bulk-to"
                    [ngModel]="createdTo()"
                    (ngModelChange)="createdTo.set($event ?? null)"
                    dateFormat="yy-mm-dd"
                    [showIcon]="true"
                    [showButtonBar]="true"
                    appendTo="body"
                    styleClass="w-full"
                />
            </div>
        </div>

        @if (validationError(); as msg) {
        <p-message severity="warn" [text]="msg" styleClass="m-0" />
        } } @if (running()) {
        <div class="flex flex-col gap-1">
            <div class="text-sm">
                Processing <strong>{{ progress() }}</strong> / {{ total() }} @if
                (currentTitle()) {
                <span class="text-muted-color">— {{ currentTitle() }}</span>
                }
            </div>
            <div
                class="bg-surface-100 dark:bg-surface-800 rounded h-2 overflow-hidden"
            >
                <div
                    class="bg-primary-500 h-full transition-[width] duration-200"
                    [style.width.%]="total() ? (progress() / total()) * 100 : 0"
                ></div>
            </div>
        </div>
        }
    </div>
    }

    <ng-template #footer>
        @if (finished()) {
        <p-button label="Close" icon="pi pi-check" (onClick)="close()" />
        } @else {
        <p-button
            label="Cancel"
            severity="secondary"
            outlined
            [disabled]="running()"
            (onClick)="close()"
        />
        <p-button
            [label]="'Replace on all (' + total() + ')'"
            icon="pi pi-refresh"
            severity="warn"
            [loading]="running()"
            [disabled]="disabled()"
            (onClick)="requestRun()"
        />
        }
    </ng-template>
</p-dialog>

<p-dialog
    [visible]="confirmVisible()"
    (visibleChange)="confirmVisible.set($event)"
    header="Replace reviews on all games?"
    [modal]="true"
    [style]="{ width: '32rem' }"
    [draggable]="false"
    [resizable]="false"
>
    <p class="m-0 text-sm">
        This removes existing fake reviews on each of the
        <strong>{{ total() }}</strong> game(s) and generates between
        <strong>{{ countMin() }}</strong> and
        <strong>{{ countMax() }}</strong> new review(s) per game, with a random
        average in <strong>{{ averageMin() }}–{{ averageMax() }}★</strong>. Real
        reviews are not affected. This cannot be undone.
    </p>

    <ng-template #footer>
        <p-button
            label="Cancel"
            severity="secondary"
            [text]="true"
            (onClick)="cancelRun()"
        />
        <p-button
            label="Replace all"
            icon="pi pi-refresh"
            severity="warn"
            (onClick)="confirmRun()"
        />
    </ng-template>
</p-dialog>
